/**
 * Короткие сигналы входа в разговор и выхода из него.
 *
 * <p>Отвечает на вопрос «я вообще зашёл?», который до сих пор не отвечало ничто: карточка меняет
 * подпись, но человек в этот момент смотрит в игру, а не в неё.
 *
 * <p>Звук синтезируется, а не берётся файлом. Две ноты — это осциллятор и огибающая, тогда как
 * файл пришлось бы класть в двух форматах: WKWebView не декодирует Ogg вовсе, и на рингтоне эти
 * грабли уже собраны (см. `callRingtone.ts`). Синтез снимает и вопрос формата, и лишний запрос,
 * и кеш.
 *
 * <p>Слышит их только тот, кто вошёл или вышел. Чужие приходы и уходы не озвучиваем: в голосе
 * сессии сидят часами и параллельно играют, щёлканье на каждого входящего заставит выключить звук
 * целиком.
 */

/** Две ноты вверх: до и соль третьей октавы. Интервал спокойный, ни на что в играх не похожий. */
const JOIN_NOTES = [523.25, 783.99];
/** Те же ноты вниз — «разговор закончился» читается без объяснений, как в лифте и в мессенджерах. */
const LEAVE_NOTES = [783.99, 523.25];
const NOTE_MS = 110;
const GAP_MS = 30;
/** Тихо: сигнал подтверждает вход, а не требует внимания. */
const PEAK_GAIN = 0.08;

type AudioContextCtor = typeof AudioContext;

const audioContextCtor = (): AudioContextCtor | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext ??
    null
  );
};

/**
 * @returns `true`, если звук пошёл. Ложь — не ошибка: браузер вправе не дать звук без жеста, а
 *     вход в разговор и выход из него из-за этого ломаться не должны.
 */
const playTones = async (notes: number[]): Promise<boolean> => {
  const Ctor = audioContextCtor();
  if (!Ctor) {
    return false;
  }

  let ctx: AudioContext | null = null;
  try {
    ctx = new Ctor();
    // Контекст рождается приостановленным, если жеста ещё не было; вход в голос — это нажатие,
    // так что обычно резюмируется молча.
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    notes.forEach((frequency, index) => {
      const startAt = ctx!.currentTime + (index * (NOTE_MS + GAP_MS)) / 1000;
      const endAt = startAt + NOTE_MS / 1000;

      const oscillator = ctx!.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      // Огибающая обязательна: прямоугольный старт и обрыв слышны как щелчок.
      const gain = ctx!.createGain();
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      oscillator.connect(gain).connect(ctx!.destination);
      oscillator.start(startAt);
      oscillator.stop(endAt + 0.02);
    });

    const total = notes.length * (NOTE_MS + GAP_MS) + 120;
    // Контекст закрываем сами: у телефона их число ограничено, и брошенные держат аудио-тракт
    // занятым — на Android это слышно как приглушение игры, пока приложение открыто.
    window.setTimeout(() => void ctx?.close().catch(() => undefined), total);
    return true;
  } catch {
    await ctx?.close().catch(() => undefined);
    return false;
  }
};

export const playJoinSound = (): Promise<boolean> => playTones(JOIN_NOTES);

/**
 * Выход озвучен так же, как вход, и по той же причине: человек уходит из голоса кнопкой в углу
 * плавающего окна, глядя при этом в игру, — подтверждение «всё, ты вышел» глазами он не увидит.
 * Играет и когда из голоса вынимает входящий звонок: там человек не нажимал ничего вовсе.
 */
export const playLeaveSound = (): Promise<boolean> => playTones(LEAVE_NOTES);
